import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import FormNumberFieldV2 from "@components/FormsV2/FormNumberFieldV2/FormNumberFieldV2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import { TriggersTable } from "./TriggersTable";
import {
  ApproversSection,
  type AvailableUser,
} from "../../ProjectApprovalV2Mspa/ApproversSection/ApproversSection";
import type {
  ApprovalLevelApprover,
  ApprovalLevelFormValues,
  ApprovalTrigger,
} from "../types/ApprovalLevel";
import "./style.css";

export interface BusinessApprovalFormProps {
  id: string;
  initialValues: ApprovalLevelFormValues;
  availableUsers: AvailableUser[];
  onSubmit: (values: ApprovalLevelFormValues) => void;
  isSubmitting?: boolean;
}

export const BusinessApprovalForm = ({
  id,
  initialValues,
  availableUsers,
  onSubmit,
  isSubmitting: _isSubmitting,
}: BusinessApprovalFormProps) => {
  const [values, setValues] = useState<ApprovalLevelFormValues>(initialValues);
  const update = <K extends keyof ApprovalLevelFormValues>(
    key: K,
    val: ApprovalLevelFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: val }));

  return (
    <Form
      id={id}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(values);
      }}
    >
      <div className="adminFormSectionTitle">Identifier & Settings</div>
      <div className="row">
        <div className="col-sm-6">
          <Form.Label htmlFor="biz-name">* Name</Form.Label>
          <TextField
            label={null}
            id="biz-name"
            value={values.name}
            placeholder="Enter a name"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update("name", e.target.value)
            }
            required
          />
        </div>
        <div className="col-sm-3">
          <FormNumberFieldV2
            label="* Approvals Required"
            id="biz-signatures"
            value={values.signaturesRequired}
            min={1}
            placeholder="Enter a number"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              update(
                "signaturesRequired",
                e.target.value === "" ? 1 : Number(e.target.value)
              )
            }
            required
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-4 businessApprovalToggleCell">
          <Form.Label>May Submitter Also Approve?</Form.Label>
          <GenericToggle
            key={`smay-${initialValues.submitterMayApprove}`}
            initialValueOfIsOn={values.submitterMayApprove}
            onToggle={(v: boolean) => update("submitterMayApprove", v)}
            onText="Yes"
            offText="No"
            hasText
          />
        </div>
        <div className="col-sm-4 businessApprovalToggleCell">
          <Form.Label>Limit Approval to Submitter's Team?</Form.Label>
          <GenericToggle
            key={`lim-${initialValues.limitApprovalsToTeam}`}
            initialValueOfIsOn={values.limitApprovalsToTeam}
            onToggle={(v: boolean) => update("limitApprovalsToTeam", v)}
            onText="Yes"
            offText="No"
            hasText
          />
        </div>
      </div>

      <div className="adminFormSectionTitle">Triggers</div>
      <TriggersTable
        triggers={values.triggers}
        onChange={(triggers: ApprovalTrigger[]) => update("triggers", triggers)}
      />

      <ApproversSection
        kind="business"
        approvers={values.approvers}
        availableUsers={availableUsers}
        availableLobs={[]}
        onChange={(approvers: ApprovalLevelApprover[]) =>
          update("approvers", approvers)
        }
      />
    </Form>
  );
};
